import React, { useEffect, useState } from "react";
import {
    Box, InputAdornment, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import FormModal from "../../../component/common/FormModal";
import FormField from "../../../component/common/FormField";
import ComboBox from "../../../component/common/ComboBox";
import VendorAutocomplete from "./vendor/VendorAutocomplete";
import { financeApi, fmtKRWFull, dailyAccountBalances } from "../api/financeApi";
import { runApiAction } from "../../../util/apiFeedback";
import { today as getToday, formatDate } from "../../../util/dateUtils";
import { formatNumberWithCommas } from "../../../util/numberFormat";
import { ds } from "../../../styles/designSystem";
import { statusOptions, SCHEDULED_STATUS_VALUES } from "../constants/cashPlanStatus";
import { CATEGORY_SUGGESTIONS, PAYMENT_TYPES } from "../constants/cashPlan";
// 상태 목록은 constants/cashPlanStatus.js 단일 원천 사용(중복 정의 제거).
const SCHEDULED_STATUSES = statusOptions(SCHEDULED_STATUS_VALUES).map(o => ({ value: o.value, label: o.label }));
// 입금(수입)에는 지급 순위 상태가 없다 — 예전엔 수입 예정에도 긴급/우선지급/홀딩을 고를 수 있어
// 현금흐름표에서 되돌릴 수 없는 상태가 만들어졌다. 수입은 '예정'만 허용한다.
const INCOME_ONLY_STATUSES = [{ value: "SCHEDULED", label: "예정" }];

const emptyForm = () => ({
    flowType: "EXPENSE", title: "", amount: "",
    scheduledDate: formatDate(getToday()),
    transactionDate: formatDate(getToday()), bankAccountId: "",
    status: "SCHEDULED",
    deptId: "", channelId: "", channelName: "", category: "",
    paymentType: "일반", vendorId: null, vendorName: "", note: "",
});

/**
 * 통합 등록 폼 — 상단 [예정]/[확정] 토글 하나로 자금계획(예정)과 실적(확정)을 모두 등록한다.
 * 현금흐름표·확정목록 두 탭이 같은 폼을 공유한다("어디에 넣지?" 고민 제거).
 *  - 예정 : 상태(예정/우선지급/홀딩) + 송금예정일(+문서결재일) → 자금계획(CashPlan) 저장
 *  - 확정 : 거래일 + 입출금 계좌 → 자금계획(완료) 저장 → 실적(Transaction) + 자금일보 자동 반영
 *
 * @param {"SCHEDULED"|"COMPLETED"} mode0 초기 토글 (현금흐름표=예정, 확정목록=확정)
 * @param {Array} accounts 은행 계좌 목록 [{ id, alias, bankName }]
 */
export default function FinanceEntryModal({
    open, onClose, onSaved, mode0 = "SCHEDULED",
    departments = [], projectChannels = [], accounts = [],
}) {
    const [mode, setMode] = useState(mode0);   // SCHEDULED = 예정, COMPLETED = 확정(실적)
    const [form, setForm] = useState(emptyForm);
    const [amountDisplay, setAmountDisplay] = useState("");
    const [saving, setSaving] = useState(false);
    const [rtBalance, setRtBalance] = useState({});   // 계좌별 실시간 잔액(오늘 미마감분 반영)

    useEffect(() => {
        if (open) { setMode(mode0); setForm(emptyForm()); setAmountDisplay(""); }
    }, [open, mode0]);

    // 확정 모드에서 오늘 자금일보를 불러와 계좌별 '실시간' 잔액(오늘 입출금까지 반영)을 구한다.
    useEffect(() => {
        if (!open || mode !== "COMPLETED") return;
        let alive = true;
        financeApi.getDailyCash(formatDate(getToday()))
            .then(r => { if (alive) setRtBalance(dailyAccountBalances(r)); })
            .catch(() => { if (alive) setRtBalance({}); });
        return () => { alive = false; };
    }, [open, mode]);

    const onAmount = (raw) => {
        const digits = String(raw).replace(/[^\d]/g, "");
        setForm(f => ({ ...f, amount: digits }));
        setAmountDisplay(digits ? formatNumberWithCommas(digits) : "");
    };

    const isDone = mode === "COMPLETED";
    const isExpense = form.flowType === "EXPENSE";
    // 선택 계좌의 현재 잔액(최근 마감 잔액) = 출금가능액. 출금 후 잔액이 마이너스면 경고(붉게).
    const selectedAccount = accounts.find(a => String(a.id) === String(form.bankAccountId)) || null;
    // 출금가능액 = 실시간 잔액(오늘 미마감분 반영). 없으면 최근 마감 잔액(currentBalance)로 대체.
    const rt = (form.bankAccountId !== "" && form.bankAccountId != null) ? rtBalance[form.bankAccountId] : null;
    const acctBalance = selectedAccount ? (rt != null ? rt : (selectedAccount.currentBalance ?? 0)) : null;
    const amountNum = Number(form.amount) || 0;
    const projected = acctBalance == null ? null : (isExpense ? acctBalance - amountNum : acctBalance + amountNum);
    const overdrawn = isDone && isExpense && projected != null && projected < 0;
    const saveDisabled = !form.title || !form.amount
        || (isDone ? (!form.transactionDate || !form.bankAccountId) : !form.scheduledDate);

    const handleSave = async () => {
        const common = {
            flowType: form.flowType,
            title: form.title,
            amount: Number(form.amount),
            category: form.category || null,
            paymentType: form.paymentType || "일반",
            deptId: form.deptId || null,
            channelId: form.channelId || null,
            channelName: form.channelName || null,
            vendorId: form.vendorId || null,
            vendorName: form.vendorName || null,
            note: form.note || null,
        };
        const payload = isDone
            ? {
                ...common,
                status: "COMPLETED",
                transactionDate: form.transactionDate,
                scheduledDate: form.transactionDate,   // 타임라인 정렬 기준일도 채워둔다
                bankAccountId: form.bankAccountId,
            }
            : {
                ...common,
                status: form.status || "SCHEDULED",
                scheduledDate: form.scheduledDate,
            };
        const result = await runApiAction({
            request: () => financeApi.createCashPlan(payload),
            successMsg: isDone ? "확정 등록되었습니다. (실적·자금일보 반영)" : "예정 등록되었습니다.",
            setLoading: setSaving,
            throwOnError: true,
        });
        if (result.ok) { onClose?.(); onSaved?.(); }
    };

    return (
        <FormModal open={open} onClose={onClose}
            title="수입 / 지출 등록"
            subtitle={isDone ? "확정 — 실적·자금일보에 즉시 반영됩니다" : "예정 — 현금흐름표 예측에 반영됩니다"}
            maxWidth="sm" onSave={handleSave} saving={saving} saveDisabled={saveDisabled}
            saveLabel={isDone ? "확정 등록" : "예정 등록"}>
            <Stack spacing={2}>
                {/* 예정 / 확정 토글 — 아래 날짜·계좌 필드가 이 값에 따라 바뀐다 */}
                <ToggleButtonGroup exclusive size="small" fullWidth color="primary" value={mode}
                    onChange={(_, v) => v && setMode(v)}
                    sx={{ "& .MuiToggleButton-root": { fontWeight: 700, fontSize: 13, py: 0.7 } }}>
                    <ToggleButton value="SCHEDULED">예정</ToggleButton>
                    <ToggleButton value="COMPLETED">확정 (실적)</ToggleButton>
                </ToggleButtonGroup>

                <FormField label="구분" required>
                    <ComboBox clearable={false}
                        options={[
                            { value: "EXPENSE", label: isDone ? "지출 (출금)" : "출금 (지출 예정)" },
                            { value: "INCOME",  label: isDone ? "수입 (입금)" : "입금 (수입 예정)" },
                        ]}
                        value={form.flowType} onChange={v => setForm(f => ({
                            ...f, flowType: v,
                            // 입금(수입) 예정은 SCHEDULED 만 허용 — EXPENSE 에서 URGENT/우선지급/홀딩을 고른 뒤
                            // INCOME 으로 바꾸면 그 상태가 그대로 저장돼 현금흐름표에서 되돌릴 수 없는 행이 됐다. INCOME 전환 시 예정으로 리셋.
                            status: v === "INCOME" ? "SCHEDULED" : f.status,
                        }))} />
                </FormField>
                <FormField label="내용" required>
                    <TextField size="small" value={form.title}
                        onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                        placeholder="행사명·거래 내용을 입력하세요" />
                </FormField>
                {isDone ? (<>
                    {/* 확정: 계좌를 먼저 골라 출금가능액을 확인한 뒤 금액을 입력한다 */}
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "nowrap" }}>
                        <FormField label="거래일" required sx={{ flex: 1, minWidth: 0 }}>
                            <TextField fullWidth size="small" type="date" value={form.transactionDate}
                                onChange={e => setForm(f => ({ ...f, transactionDate: e.target.value }))} />
                        </FormField>
                        <FormField label="입출금 계좌" required sx={{ flex: 1, minWidth: 0 }}>
                            <ComboBox placeholder="계좌 선택"
                                options={accounts.map(a => ({
                                    value: a.id,
                                    label: `${a.alias || a.bankName} · ${fmtKRWFull(rtBalance[a.id] != null ? rtBalance[a.id] : (a.currentBalance ?? 0))}`,
                                }))}
                                value={form.bankAccountId}
                                onChange={v => setForm(f => ({ ...f, bankAccountId: v }))} />
                        </FormField>
                    </Box>
                    <FormField label="금액" required>
                        <TextField size="small" value={amountDisplay}
                            onChange={e => onAmount(e.target.value)}
                            error={overdrawn}
                            InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }}
                            inputProps={{ inputMode: "numeric" }} placeholder="0" />
                    </FormField>
                    {form.bankAccountId && (
                        <Box sx={{
                            mt: -1, px: 1.25, py: 0.9, borderRadius: 1,
                            bgcolor: overdrawn ? "#fef2f2" : "#f8fafc",
                            border: `1px solid ${overdrawn ? "#fecaca" : "#e2e8f0"}`,
                            display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap",
                        }}>
                            <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                                {isExpense ? "출금가능액" : "현재 잔액"}{" "}
                                <b style={{ color: "#334155" }}>{fmtKRWFull(acctBalance ?? 0)}</b>
                            </Typography>
                            {form.amount !== "" && (
                                <Typography sx={{ fontSize: 12, fontWeight: 800, color: overdrawn ? ds.finance.expense : "#0f766e" }}>
                                    {isExpense ? "출금 후 잔액 " : "입금 후 잔액 "}
                                    {fmtKRWFull(projected ?? 0)}
                                    {overdrawn ? " · 잔액 부족" : ""}
                                </Typography>
                            )}
                        </Box>
                    )}
                </>) : (<>
                    <FormField label="금액" required>
                        <TextField size="small" value={amountDisplay}
                            onChange={e => onAmount(e.target.value)}
                            InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }}
                            inputProps={{ inputMode: "numeric" }} placeholder="0" />
                    </FormField>
                    <FormField label={form.flowType === "INCOME" ? "입금 예정일" : "송금 예정일"} required>
                        <TextField fullWidth size="small" type="date" value={form.scheduledDate}
                            onChange={e => setForm(f => ({ ...f, scheduledDate: e.target.value }))} />
                    </FormField>
                    <FormField label="상태">
                        <ComboBox clearable={false} options={form.flowType === "INCOME" ? INCOME_ONLY_STATUSES : SCHEDULED_STATUSES}
                            value={form.status} onChange={v => setForm(f => ({ ...f, status: v }))} />
                    </FormField>
                </>)}

                <FormField label="팀">
                    <ComboBox placeholder="선택 안 함"
                        options={departments.map(d => ({ value: d.deptId, label: d.deptName }))}
                        value={form.deptId} onChange={v => setForm(f => ({ ...f, deptId: v }))} />
                </FormField>
                <FormField label="프로젝트 채널">
                    <ComboBox placeholder="선택 안 함"
                        options={projectChannels.map(c => ({ value: c.id, label: c.name }))}
                        value={form.channelId}
                        onChange={v => {
                            const ch = projectChannels.find(c => String(c.id) === String(v));
                            setForm(f => ({ ...f, channelId: v, channelName: ch ? ch.name : "" }));
                        }} />
                </FormField>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                    <FormField label="거래내용 구분" sx={{ flex: 2, minWidth: 0 }}>
                        <ComboBox freeSolo placeholder="프로젝트 / 사업소득 / 고정비 등"
                            options={CATEGORY_SUGGESTIONS}
                            value={form.category} onChange={v => setForm(f => ({ ...f, category: v }))} />
                    </FormField>
                    <FormField label="결제 유형" sx={{ flex: 1, minWidth: 110 }}>
                        <ComboBox freeSolo placeholder="일반 / 고정비"
                            options={PAYMENT_TYPES}
                            value={form.paymentType || "일반"} onChange={v => setForm(f => ({ ...f, paymentType: v }))} />
                    </FormField>
                </Box>
                <FormField label="거래처">
                    <VendorAutocomplete freeSolo value={form.vendorId} initialName={form.vendorName}
                        onChange={(id, vendorOrText) => {
                            if (typeof vendorOrText === "string") {
                                setForm(f => ({ ...f, vendorId: null, vendorName: vendorOrText }));
                            } else if (vendorOrText) {
                                setForm(f => ({ ...f, vendorId: id, vendorName: vendorOrText.name }));
                            } else {
                                setForm(f => ({ ...f, vendorId: null, vendorName: "" }));
                            }
                        }}
                        label={null} helperText="등록된 거래처에서 선택하거나 그대로 입력하세요" />
                </FormField>
                <FormField label="비고">
                    <TextField size="small" multiline rows={2} value={form.note}
                        onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
                </FormField>

                {isDone && (
                    <Typography sx={{ fontSize: 11.5, color: "#94a3b8", mt: -0.5 }}>
                        확정 등록하면 실적(확정 매출/지출)과 선택한 계좌의 자금일보에 자동으로 반영됩니다.
                    </Typography>
                )}
            </Stack>
        </FormModal>
    );
}
